import React, { useEffect, useState } from "react";
import { getFirestore, collection, query, getDocs, doc, getDoc, where, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { List, message, Avatar, Button, Tooltip, Typography, Spin, Input, Space } from 'antd';
import { getAuth } from "firebase/auth";
import MediumModal from "../../components/Components/Modals/MediumModal";
import ImagePositioningModal from "../modal/ImagePositioningModal";
import { AppstoreOutlined, UnorderedListOutlined, HistoryOutlined, DownloadOutlined, EditOutlined, CheckOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import HeaderComponent from '../../components/Components/Headers/HeaderComponent';
import SimpleCard from '../../components/Components/Cards/SimpleCard';
import { primaryShades } from "../../theme/theme";
import { secureGetItem } from "../../data/utils";
import { generateExcelReport } from './ReportBrand';
import ButtonState from '../../components/Components/Buttons/ButtonState';

// Per ogni ordine del catalogo:
// 1. Vai direttamente alle Variants
// 2. Cerca tutte le varianti con catalogOrdersRef = ordine.id
// 3. Conta gli stati delle varianti trovate
// 4. Mostra i pallini colorati con i conteggi

// Importazione dell'icona Orders personalizzata
import ARShadesOrdersIcon from '../../assets/icon/ARShades Orders icon2.png';

const { Text } = Typography;

function Home() {
    // Stato per memorizzare gli ordini del catalogo recuperati dal database
    const [catalogOrders, setCatalogOrders] = useState([]);
    const [viewMode, setViewMode] = useState("card"); // Changed initial state to "card"
    const [userRole, setUserRole] = useState("");
    const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editingOrderId, setEditingOrderId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [uploadingImage, setUploadingImage] = useState(false);
    const [isImageModalVisible, setIsImageModalVisible] = useState(false);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [currentOrderId, setCurrentOrderId] = useState(null);
    const [profiles, setProfiles] = useState({});
    // Inizializzazione di Firestore e della funzione di navigazione
    const db = getFirestore();
    const navigate = useNavigate();
    const auth = getAuth();

    const switchToCardView = () => {
        setViewMode("card");
        localStorage.setItem("viewMode", "card");
    };

    const switchToRowView = () => {
        setViewMode("row");
        localStorage.setItem("viewMode", "row");
    };

    // Funzione per iniziare la modifica del nome
    const handleStartEditName = (order, event) => {
        event.stopPropagation(); // Previene il click sul card/row
        setEditingOrderId(order.id);
        setEditingName(order.orderName || "Undefined");
    };

    // Funzione per salvare il nuovo nome
    const handleSaveName = async (orderId, event) => {
        event.stopPropagation();
        if (!editingName.trim()) {
            message.error("Nome non valido");
            return;
        }

        try {
            const orderRef = doc(db, "CatalogOrders", orderId);
            await updateDoc(orderRef, {
                orderName: editingName.trim()
            });

            // Aggiorna lo stato locale
            setCatalogOrders(prevOrders =>
                prevOrders.map(order =>
                    order.id === orderId
                        ? { ...order, displayName: editingName.trim(), orderName: editingName.trim() }
                        : order
                )
            );

            setEditingOrderId(null);
            setEditingName("");
            message.success("Nome aggiornato con successo!");
        } catch (error) {
            console.error("Errore nell'aggiornamento del nome:", error);
            message.error("Errore nell'aggiornamento del nome");
        }
    };

    // Funzione per annullare la modifica
    const handleCancelEdit = (event) => {
        event.stopPropagation();
        setEditingOrderId(null);
        setEditingName("");
    };

    // Funzione per gestire la selezione del file e aprire la modale
    const handleImageSelection = (file, orderId) => {
        setSelectedImageFile(file);
        setCurrentOrderId(orderId);
        setIsImageModalVisible(true);
    };

    // Funzione per gestire il caricamento dell'immagine finale
    const handleImageUpload = async (file, orderId) => {
        setUploadingImage(true);
        try {
            const storage = getStorage();
            const imageRef = ref(storage, `CatalogOrders/${orderId}/image/${file.name}`);

            // Carica l'immagine
            const snapshot = await uploadBytes(imageRef, file);

            // Ottieni l'URL di download
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Aggiorna il documento Firebase con l'URL dell'immagine
            const orderRef = doc(db, "CatalogOrders", orderId);
            await updateDoc(orderRef, {
                image: downloadURL
            });

            // Aggiorna lo stato locale per mostrare immediatamente l'immagine
            setCatalogOrders(prevOrders =>
                prevOrders.map(order =>
                    order.id === orderId
                        ? { ...order, image: downloadURL }
                        : order
                )
            );

            message.success("Image uploaded successfully!");
        } catch (error) {
            console.error("Errore nel caricamento dell'immagine:", error);
            message.error("Errore nel caricamento dell'immagine");
        } finally {
            setUploadingImage(false);
        }
    };

    // Funzioni per gestire la modale di posizionamento immagine
    const handleImageModalConfirm = async (croppedFile) => {
        if (croppedFile && currentOrderId) {
            await handleImageUpload(croppedFile, currentOrderId);
            handleImageModalCancel();
        }
    };

    const handleImageModalCancel = () => {
        setIsImageModalVisible(false);
        setSelectedImageFile(null);
        setCurrentOrderId(null);
    };

    // Funzione per recuperare i dati dei profili dalla collezione 'Profiles'
    const fetchProfiles = async () => {
        try {
            const profilesQuery = query(collection(db, "Profiles"));
            const querySnapshot = await getDocs(profilesQuery);
            const profilesData = {};

            querySnapshot.docs.forEach(doc => {
                const profileData = doc.data();
                // Usa l'ID del documento come chiave (che è l'email)
                profilesData[doc.id] = {
                    firstName: profileData.firstName || '',
                    lastName: profileData.lastName || '',
                    fullName: `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim()
                };
            });

            setProfiles(profilesData);
        } catch (error) {
            console.error("Errore nel recupero dei profili:", error);
        }
    };

    // Funzione helper per ottenere il nome completo dal profilo
    const getProfileDisplayName = (email) => {
        if (!email || !profiles[email]) {
            return 'N/A';
        }
        return profiles[email].fullName || email;
    };

    // Funzione helper per formattare la data dell'ordine
    const formatOrderDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            const months = [
                'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
                'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'
            ];

            const day = date.getDate();
            const month = months[date.getMonth()];
            const year = date.getFullYear();

            return `${day} ${month} ${year}`;
        } catch (error) {
            console.error("Errore nel formattare la data:", error);
            return 'N/A';
        }
    };

    // Funzione per rimuovere l'immagine direttamente (bottone delete)
    const handleDirectRemoveImage = async (orderId, e) => {
        e.stopPropagation();

        try {
            const orderRef = doc(db, "CatalogOrders", orderId);
            await updateDoc(orderRef, {
                image: null
            });

            // Aggiorna lo stato locale per rimuovere l'immagine
            setCatalogOrders(prevOrders =>
                prevOrders.map(order =>
                    order.id === orderId
                        ? { ...order, image: null }
                        : order
                )
            );

            message.success("Image removed successfully!");
        } catch (error) {
            console.error("Error removing image:", error);
            message.error("Error removing image");
        }
    };

    // Carica la vista salvata da localStorage e recupera il ruolo dell'utente
    useEffect(() => {
        const savedViewMode = localStorage.getItem("viewMode");
        if (savedViewMode) {
            setViewMode(savedViewMode);
        }

        const role = secureGetItem("role");
        setUserRole(role);
    }, []);

    // Carica gli ordini del catalogo filtrati per il profilo dell'utente autenticato
    useEffect(() => {
        const fetchCatalogOrders = async () => {
            try {
                setLoading(true);
                const user = auth.currentUser;
                if (!user) {
                    message.error("Utente non autenticato");
                    setLoading(false);
                    return;
                }

                // Ottieni l'email dell'utente autenticato dal secure storage
                const userEmail = secureGetItem("email");
                if (!userEmail) {
                    message.error("Email utente non trovata");
                    setLoading(false);
                    return;
                }

                // Recupera i profili
                await fetchProfiles();

                // Recupera il profilo dell'utente autenticato per ottenere list_3d_catalogOrders
                // Questo array contiene gli ID dei catalogOrders specifici per questo utente
                const userProfileRef = doc(db, "Profiles", userEmail);
                const userProfileSnap = await getDoc(userProfileRef);

                if (!userProfileSnap.exists()) {
                    message.error("Profilo utente non trovato");
                    setLoading(false);
                    return;
                }

                const userProfileData = userProfileSnap.data();
                const userCatalogOrderIds = userProfileData.list_3d_catalogOrders || [];

                console.log(`Found ${userCatalogOrderIds.length} catalog orders for user: ${userEmail}`, userCatalogOrderIds);

                if (userCatalogOrderIds.length === 0) {
                    // Se l'utente non ha catalog orders, mostra una lista vuota
                    setCatalogOrders([]);
                    setLoading(false);
                    return;
                }

                // Query per recuperare solo i CatalogOrders dell'utente autenticato
                // Nota: Firebase ha un limite di 10 elementi per le query 'in', quindi dividiamo in chunks se necessario
                const chunks = [];
                for (let i = 0; i < userCatalogOrderIds.length; i += 10) {
                    chunks.push(userCatalogOrderIds.slice(i, i + 10));
                }

                let allOrders = [];
                for (const chunk of chunks) {
                    const q = query(
                        collection(db, "CatalogOrders"),
                        where("__name__", "in", chunk)
                    );
                    const querySnapshot = await getDocs(q);
                    const chunkOrders = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    allOrders = allOrders.concat(chunkOrders);
                }

                const ordersList = allOrders;

                // Elabora ogni ordine per calcolare i conteggi degli stati delle varianti
                await Promise.all(ordersList.map(async (order) => {
                    try {
                        // Nuova logica: recupera direttamente le varianti tramite catalogOrdersRef
                        // per calcolare gli stati dei pallini senza passare per i prodotti
                        const variantsQuery = query(
                            collection(db, "Variants"),
                            where("catalogOrdersRef", "==", order.id)
                        );
                        const variantsSnapshot = await getDocs(variantsQuery);
                        const allVariants = variantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                        if (allVariants.length > 0) {
                            const filteredVariants = allVariants.filter(variant => variant.status !== "Pubblicato");

                            const statusMapping = {
                                "In Pubblicazione": "In Publication",
                                "Rev. Cliente": "Client Rev.",
                                "Rev. Spaarkly": "Spaarkly Rev.",
                                "Rev. Modellista": "Modelist Rev.",
                                "Incompleto": "Incomplete"
                            };

                            const statusCounts = {};
                            for (const variant of filteredVariants) {
                                const status = statusMapping[variant.status] || variant.status;
                                statusCounts[status] = (statusCounts[status] || 0) + 1;
                            }

                            // Calcola il numero di prodotti unici dalle varianti
                            const uniqueProductRefs = [...new Set(allVariants.map(variant => variant.productRef))];

                            order.statusCounts = statusCounts;
                            order.totalAssets = filteredVariants.length;
                            order.totalAllAssets = allVariants.length;
                            order.totalSelectedAssets = uniqueProductRefs.length; // Numero di prodotti unici dalle varianti
                        } else {
                            order.statusCounts = {};
                            order.totalAssets = 0;
                            order.totalAllAssets = 0;
                            order.totalSelectedAssets = 0;
                        }
                    } catch (error) {
                        console.error(`Errore nel caricamento dati per ordine ${order.id}:`, error);
                        order.statusCounts = {};
                        order.totalAssets = 0;
                        order.totalAllAssets = 0;
                        order.totalSelectedAssets = 0;
                    }

                    // Aggiungi informazioni aggiuntive per la visualizzazione
                    order.displayName = order.orderName || "Undefined";
                    order.clientProfile = order.profile || "N/A";
                }));
                setCatalogOrders(ordersList);
            } catch (error) {
                console.error("Errore nel recupero degli ordini del catalogo: ", error);
                message.error(`Errore nel recupero degli ordini del catalogo: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchCatalogOrders();
    }, [db, auth]);

    // Mostra la modale di conferma per il download del report
    const showSummaryModal = () => {
        setIsSummaryModalVisible(true);
    };

    // Chiude la modale di conferma
    const handleCloseSummaryModal = () => {
        setIsSummaryModalVisible(false);
    };

    // Funzione per generare il report dopo la conferma
    const handleGenerateReport = async () => {
        try {
            if (!catalogOrders || catalogOrders.length === 0) {
                message.error({ content: 'No catalog orders available for the report', key: 'report' });
                return;
            }

            message.loading({ content: 'Generating report...', key: 'report' });

            await generateExcelReport(catalogOrders);
            message.success({ content: 'Report generated successfully!', key: 'report', duration: 2 });
            handleCloseSummaryModal();

        } catch (error) {
            message.error({
                content: `Error generating report: ${error.message}`,
                key: 'report',
                duration: 4
            });
        }
    };

    // Rendering dell'interfaccia utente con una lista di marchi principali
    return (
        <div>
            <HeaderComponent
                title="Catalogues Orders"
                isVisible={false}
            />

            {/* Pulsanti per la vista e il log per Admin, Modellista e ModellerSupervisor */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {(userRole === "Admin" || userRole === "Modellista" || userRole === "ModellerSupervisor") && (
                        <>
                            <Button
                                type="primary"
                                icon={<HistoryOutlined />}
                                onClick={() => navigate('/framevalidation/log')}
                            >
                                Log
                            </Button>
                            {/* Button for testing notifications - Development only
                            <Button
                                type="primary"
                                icon={<BellOutlined />}
                                onClick={() => navigate('/framevalidation/test-notifications')}
                            >
                                Test Notifiche
                            </Button>
                            */}
                        </>
                    )}
                    {(userRole === "Admin" || userRole === "Cliente") && (
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={showSummaryModal}
                        >
                            Download Summary
                        </Button>
                    )}
                </div>
                <div
                    style={{
                        display: "inline-flex",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        overflow: "hidden",
                        marginLeft: userRole === "Admin" ? "0" : "auto"
                    }}
                >
                    <div
                        onClick={switchToCardView}
                        style={{
                            padding: "8px 16px",
                            cursor: "pointer",
                            backgroundColor: viewMode === "card" ? primaryShades.primary300 : "#fff",
                            color: viewMode === "card" ? "#fff" : "#000",
                            borderRight: "1px solid #ccc",
                        }}
                    >
                        <AppstoreOutlined />
                    </div>
                    <div
                        onClick={switchToRowView}
                        style={{
                            padding: "8px 16px",
                            cursor: "pointer",
                            backgroundColor: viewMode === "row" ? primaryShades.primary300 : "#fff",
                            color: viewMode === "row" ? "#fff" : "#000",
                        }}
                    >
                        <UnorderedListOutlined />
                    </div>
                </div>
            </div>

            {/* Spinner di caricamento */}
            {loading ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px'
                }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    {/* Lista degli ordini del catalogo in vista a riga */}
                    {viewMode === "row" ? (
                        <List
                            dataSource={catalogOrders}
                            renderItem={order => (
                                <SimpleCard
                                    onClick={() => navigate(`/framevalidation/${order.id}`)}
                                    style={{ marginBottom: '16px' }}
                                >
                                    <List.Item
                                        key={order.id}
                                        style={{ display: 'flex', justifyContent: 'space-between' }}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                    {order.image ? (
                                                        <Avatar size={40} src={order.image} />
                                                    ) : (
                                                        <div
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                borderRadius: '50%',
                                                                backgroundColor: primaryShades.primary300,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <img
                                                                src={ARShadesOrdersIcon}
                                                                alt="Orders"
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    filter: 'brightness(0) invert(1)'
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Icona upload/delete per vista a righe */}
                                                    {order.image ? (
                                                        <Tooltip title="Remove Image" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                            <DeleteOutlined
                                                                onClick={(e) => handleDirectRemoveImage(order.id, e)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '-2px',
                                                                    right: '-2px',
                                                                    fontSize: '12px',
                                                                    color: '#ff4d4f',
                                                                    cursor: 'pointer',
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '50%',
                                                                    padding: '2px',
                                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                                    zIndex: 5
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Upload Image" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                            <UploadOutlined
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    document.getElementById(`upload-home-row-${order.id}`).click();
                                                                }}
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '-2px',
                                                                    right: '-2px',
                                                                    fontSize: '12px',
                                                                    color: primaryShades.primary300,
                                                                    cursor: uploadingImage ? 'not-allowed' : 'pointer',
                                                                    opacity: uploadingImage ? 0.5 : 1,
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '50%',
                                                                    padding: '2px',
                                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                                    zIndex: 5
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            }
                                            title={
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {editingOrderId === order.id ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <Input
                                                                value={editingName}
                                                                onChange={(e) => setEditingName(e.target.value)}
                                                                onPressEnter={(e) => handleSaveName(order.id, e)}
                                                                style={{ fontWeight: 'bold', fontSize: '14px', minWidth: '200px' }}
                                                                autoFocus
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <Space style={{ marginLeft: '8px' }}>
                                                                <Button
                                                                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                                                    type="primary"
                                                                    shape="circle"
                                                                    size="small"
                                                                    icon={<CheckOutlined />}
                                                                    onClick={(e) => handleSaveName(order.id, e)}
                                                                />
                                                                <Button
                                                                    style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: 'white' }}
                                                                    shape="circle"
                                                                    size="small"
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={handleCancelEdit}
                                                                />
                                                            </Space>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ fontWeight: 'bold', color: '#333' }}>{order.displayName}</span>
                                                            <Tooltip title="Edit name" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                                <EditOutlined
                                                                    onClick={(e) => handleStartEditName(order, e)}
                                                                    style={{
                                                                        fontSize: '14px',
                                                                        color: primaryShades.primary300,
                                                                        cursor: 'pointer'
                                                                    }}
                                                                />
                                                            </Tooltip>

                                                        </div>
                                                    )}
                                                </div>
                                            }
                                            description={
                                                <div style={{ lineHeight: '1.4' }}>
                                                    <Text type="secondary" style={{ display: 'block', fontSize: '13px', marginBottom: '2px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        Ordered by: <span style={{ fontWeight: '500' }}>{getProfileDisplayName(order.profile)}</span>
                                                    </Text>
                                                    <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '6px', color: '#888' }}>
                                                        {formatOrderDate(order.timestamp)}
                                                    </Text>
                                                    <Text type="secondary" style={{ fontSize: '14px', fontWeight: '500', marginTop: '10px', display: 'block' }}>{order.totalAssets} Assets Found</Text>
                                                </div>
                                            }
                                        />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {order.statusCounts && Object.entries(order.statusCounts).map(([status, count]) => (
                                                <Tooltip key={status} title={status} color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                    <ButtonState
                                                        state={status}
                                                        text={count}
                                                        isCircle={true}
                                                        style={{
                                                            width: "24px",
                                                            height: "24px",
                                                            borderRadius: "50%",
                                                            fontSize: "11px",
                                                            fontWeight: "bold",
                                                            padding: 0,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            cursor: "default"
                                                        }}
                                                    />
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </List.Item>
                                </SimpleCard>
                            )}
                        />
                    ) : (
                        // Lista degli ordini del catalogo in vista a card
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px', alignItems: 'stretch' }}>
                            {catalogOrders.map(order => (
                                <div className="hoverable-card" key={order.id} style={{ width: '100%', height: '100%' }}>
                                    <SimpleCard
                                        onClick={() => navigate(`/framevalidation/${order.id}`)}
                                        style={{ height: '100%' }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '18px',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                            position: 'relative'
                                        }}>
                                            {/* Icona matita in alto a destra */}
                                            {editingOrderId !== order.id && (
                                                <Tooltip title="Edit name" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                    <EditOutlined
                                                        onClick={(e) => handleStartEditName(order, e)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '8px',
                                                            right: '8px',
                                                            fontSize: '16px',
                                                            color: primaryShades.primary300,
                                                            cursor: 'pointer',
                                                            zIndex: 10
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}

                                            {/* Icone salva/annulla in alto a destra durante modifica */}
                                            {editingOrderId === order.id && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    display: 'flex',
                                                    gap: '8px',
                                                    zIndex: 10
                                                }}>
                                                    <Button
                                                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                                        type="primary"
                                                        shape="circle"
                                                        size="small"
                                                        icon={<CheckOutlined />}
                                                        onClick={(e) => handleSaveName(order.id, e)}
                                                    />
                                                    <Button
                                                        style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: 'white' }}
                                                        shape="circle"
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={handleCancelEdit}
                                                    />
                                                </div>
                                            )}
                                            {/* Icona immagine */}
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                {order.image ? (
                                                    <div style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: '50%',
                                                        overflow: 'hidden',
                                                        marginBottom: '16px',
                                                        border: `2px solid ${primaryShades.primary300}`
                                                    }}>
                                                        <img
                                                            src={order.image}
                                                            alt="Order"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: '50%',
                                                        backgroundColor: primaryShades.primary300,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '32px',
                                                        color: 'white',
                                                        marginBottom: '16px'
                                                    }}>
                                                        <img
                                                            src={ARShadesOrdersIcon}
                                                            alt="Orders"
                                                            style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                filter: 'brightness(0) invert(1)'
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                {/* Icona upload/delete sotto il cerchio */}
                                                {editingOrderId !== order.id && (
                                                    order.image ? (
                                                        <Tooltip title="Remove Image" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                            <DeleteOutlined
                                                                onClick={(e) => handleDirectRemoveImage(order.id, e)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '10px',
                                                                    right: '-5px',
                                                                    fontSize: '16px',
                                                                    color: '#ff4d4f',
                                                                    cursor: 'pointer',
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '50%',
                                                                    padding: '4px',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                    zIndex: 5
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Upload Image" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                            <UploadOutlined
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    document.getElementById(`upload-home-card-${order.id}`).click();
                                                                }}
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '10px',
                                                                    right: '-5px',
                                                                    fontSize: '16px',
                                                                    color: primaryShades.primary300,
                                                                    cursor: uploadingImage ? 'not-allowed' : 'pointer',
                                                                    opacity: uploadingImage ? 0.5 : 1,
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '50%',
                                                                    padding: '4px',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                    zIndex: 5
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )
                                                )}
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                {editingOrderId === order.id ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                        <Input
                                                            value={editingName}
                                                            onChange={(e) => setEditingName(e.target.value)}
                                                            onPressEnter={(e) => handleSaveName(order.id, e)}
                                                            style={{
                                                                fontWeight: '600',
                                                                fontSize: '16px',
                                                                textAlign: 'center',
                                                                minWidth: '150px',
                                                                maxWidth: '180px'
                                                            }}
                                                            autoFocus
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                ) : (
                                                    <h3 style={{ fontWeight: '600', color: '#333', margin: 0, fontSize: '16px', marginBottom: '8px' }}>{order.displayName}</h3>
                                                )}
                                                <Text type="secondary" style={{ display: 'block', marginBottom: '2px', fontSize: '13px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    Ordered by: <span style={{ fontWeight: '500' }}>{getProfileDisplayName(order.profile)}</span>
                                                </Text>
                                                <Text type="secondary" style={{ display: 'block', marginBottom: '10px', fontSize: '12px', color: '#888' }}>
                                                    {formatOrderDate(order.timestamp)}
                                                </Text>
                                                <Text type="secondary" style={{ display: 'block', marginBottom: '12px', marginTop: '25px', fontSize: '14px', fontWeight: '500' }}>
                                                    {order.totalAssets} Assets Found
                                                </Text>
                                                <div style={{ display: 'block', marginTop: '8px' }}>
                                                    {order.statusCounts && Object.entries(order.statusCounts).map(([status, count]) => (
                                                        <Tooltip key={status} title={status} color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                            <ButtonState
                                                                state={status}
                                                                text={count}
                                                                isCircle={true}
                                                                style={{
                                                                    padding: 0,
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    marginRight: "5px",
                                                                    marginTop: "5px"
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </SimpleCard>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Input file nascosti per l'upload delle immagini */}
                    {catalogOrders.map(order => (
                        <div key={`uploads-${order.id}`}>
                            {/* Input per vista a righe */}
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id={`upload-home-row-${order.id}`}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        handleImageSelection(file, order.id);
                                    }
                                    e.target.value = null; // Reset per permettere ricaricamento dello stesso file
                                }}
                            />
                            {/* Input per vista a card */}
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id={`upload-home-card-${order.id}`}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        handleImageSelection(file, order.id);
                                    }
                                    e.target.value = null; // Reset per permettere ricaricamento dello stesso file
                                }}
                            />
                        </div>
                    ))}
                </>
            )}

            {/* Modale per il posizionamento dell'immagine */}
            <ImagePositioningModal
                visible={isImageModalVisible}
                onCancel={handleImageModalCancel}
                onConfirm={handleImageModalConfirm}
                imageFile={selectedImageFile}
                loading={uploadingImage}
            />

            {/* Modale per la conferma del download del riepilogo */}
            <MediumModal
                visible={isSummaryModalVisible}
                onClose={handleCloseSummaryModal}
                title="Download Summary"
                onConfirm={handleGenerateReport}
                cancelText="Cancel"
                confirmText="Download"
            >
                <div>
                    <p style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)', marginBottom: '1rem' }}>
                        You are about to download an Excel file containing completed summary for the catalog orders with working 3D assets.
                    </p>
                    <p style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                        The file will contain one sheet with:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', marginBottom: '1rem' }}>
                        <li>A summary with the number of total 3D assets for each order</li>
                        <li>A detailed breakdown of assets by status (Orderable, etc.)</li>
                        <li>Client information and order details</li>
                    </ul>
                    <p style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', fontStyle: 'italic' }}>
                        Note: If the selected orders have many assets, the generation process may take a few moments.
                    </p>
                </div>
            </MediumModal>
        </div>
    );
}

export default Home;